/**
 * WebSocket Hook for Real-Time Communication (V2)
 * Connects to the new Supabase-based Team Comms system
 */
import { useEffect, useRef, useCallback, useState } from 'react';

// Convert HTTP URL to WebSocket URL
const getWebSocketUrl = () => {
  const apiUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  try {
    const url = new URL(apiUrl);
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${url.host}`;
  } catch (err) {
    // Fallback to current host
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${window.location.host}`;
  }
};

export const useWebSocket = (userId, churchId, onMessage) => {
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const connectRef = useRef(null);
  const pongReceivedRef = useRef(true);

  const connect = useCallback(() => {
    if (!userId || !churchId) {
      console.log('[WebSocket] Missing userId or churchId, not connecting');
      return;
    }
    
    // Don't reconnect if already connected with valid connection
    if (ws.current?.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Already connected, skipping reconnect');
      return;
    }
    
    // Don't reconnect if connecting
    if (ws.current?.readyState === WebSocket.CONNECTING) {
      console.log('[WebSocket] Connection in progress, skipping');
      return;
    }
    
    const baseUrl = getWebSocketUrl();
    // Use V2 WebSocket endpoint
    const wsUrl = `${baseUrl}/api/comms/v3/ws?user_id=${encodeURIComponent(userId)}&church_id=${encodeURIComponent(churchId)}`;
    
    console.log('[WebSocket] Connecting to:', wsUrl);
    
    try {
      ws.current = new WebSocket(wsUrl);
      
      ws.current.onopen = () => {
        console.log('[WebSocket] Connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
        
        // Start ping interval to keep connection alive
        pongReceivedRef.current = true;
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            // Pong timeout: if last ping was never answered, connection is dead
            if (!pongReceivedRef.current) {
              console.warn('[WebSocket] Pong not received, closing dead connection');
              ws.current.close();
              return;
            }
            pongReceivedRef.current = false;
            ws.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };
      
      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle pong (keep-alive response)
          if (data.type === 'pong') {
            pongReceivedRef.current = true;
            return;
          }
          
          // (R28: `online_users` / `presence_update` handlers removed — the server
          // never emits them; presence is Firestore-driven.)

          // Forward all messages to the callback
          if (onMessage) {
            onMessage(data);
          }
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err);
        }
      };
      
      ws.current.onclose = (event) => {
        console.log('[WebSocket] Disconnected', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }
        
        // Attempt to reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          
          reconnectTimeout.current = setTimeout(() => {
            connectRef.current?.();
          }, delay);
        }
      };
      
      ws.current.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };
      
    } catch (err) {
      console.error('[WebSocket] Connection error:', err);
    }
  }, [userId, churchId, onMessage]);

  // Keep connectRef updated for recursive calls
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // Send a message through the WebSocket
  const sendMessage = useCallback((data) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(data));
    }
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((conversationId, participantIds, userName, isTyping) => {
    sendMessage({
      type: 'typing',
      conversation_id: conversationId,
      participant_ids: participantIds,
      user_name: userName,
      is_typing: isTyping
    });
  }, [sendMessage]);


  // Connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      // Cleanup on unmount
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (pingInterval.current) {
        clearInterval(pingInterval.current);
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [connect]);

  // Reconnect when visibility changes (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && (!ws.current || ws.current.readyState !== WebSocket.OPEN)) {
        reconnectAttempts.current = 0;
        connectRef.current?.();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return {
    isConnected,
    sendMessage,
    sendTyping
  };
};

export default useWebSocket;
